import { Comment_Base } from '~/common/typescript/models/Comment';
import { GeoJSONSourceSpecification } from 'maplibre-gl';
import maplibregl from 'maplibre-gl';
import { IconUtil } from '~/utils/IconUtil';
import { getHashColor } from '~/utils/hashStringUtil';
import { renderToString } from 'react-dom/server';
import { PiChatCircleDots } from 'react-icons/pi';

export const CommentMapUtilOptimized = {
  _sourceId: 'comments-source-optimized',
  _layerId: 'comments-layer-optimized',

  // Cache management
  private_commentsOnMap: new Map<number, Comment_Base>(),
  private_commentHashes: new Map<number, string>(),
  private_bitmapCache: new Map<string, string>(), // username -> bitmapName
  private_labelOverlays: new Map<number, HTMLDivElement>(), // commentId -> div element

  /**
   * Compute hash for a comment to detect changes
   */
  computeCommentHash(comment: Comment_Base): string {
    const hashData = {
      id: comment.id,
      title: comment.title,
      description: comment.description,
      resolved: comment.resolved,
      geojson: comment.geojson,
      userId: comment.user?.id,
      username: comment.user?.username,
      fullname: comment.user?.fullname,
    };
    return JSON.stringify(hashData);
  },

  /**
   * Generate bitmap name for a user
   */
  async getBitmapForUser(comment: Comment_Base): Promise<string> {
    const username = comment.user?.username || 'NoName';
    const fullname = comment.user?.fullname || 'No Name';

    // Check cache first, but also verify the image still exists in map
    if (this.private_bitmapCache.has(username)) {
      const cachedBitmapName = this.private_bitmapCache.get(username)!;
      if (window.map.hasImage(cachedBitmapName)) {
        return cachedBitmapName;
      } else {
        // Image was removed (probably due to style reload), remove from cache
        this.private_bitmapCache.delete(username);
      }
    }

    // Generate new bitmap
    const bitmapName = `comment-avatar-${username}-${Date.now()}`;
    const text = fullname.slice(0, 2);
    const { backgroundColor, foregroundColor } = getHashColor(username);
    const bitmap = await IconUtil.getCommentAvatarBitmap(text, backgroundColor, foregroundColor);

    if (!window.map.hasImage(bitmapName)) {
      window.map.addImage(bitmapName, bitmap!);
    }

    // Cache the bitmap name
    this.private_bitmapCache.set(username, bitmapName);
    return bitmapName;
  },

  /**
   * Compute diff between current and new comments
   */
  computeCommentsDiff(newComments: Comment_Base[]) {
    const newCommentsMap = new Map(newComments.map(c => [c.id!, c]));
    const currentCommentsMap = new Map(Array.from(this.private_commentsOnMap.entries()));

    const toAdd: Comment_Base[] = [];
    const toUpdate: Comment_Base[] = [];
    const toRemove: Comment_Base[] = [];

    // Check for new and updated comments
    for (const comment of newComments) {
      const commentId = comment.id!;
      const newHash = this.computeCommentHash(comment);
      const oldHash = this.private_commentHashes.get(commentId);

      if (!this.private_commentsOnMap.has(commentId)) {
        // New comment
        toAdd.push(comment);
      } else if (oldHash !== newHash) {
        // Updated comment
        toUpdate.push(comment);
      }
    }

    // Check for removed comments
    for (const [commentId, comment] of currentCommentsMap) {
      if (!newCommentsMap.has(commentId)) {
        toRemove.push(comment);
      }
    }

    return { toAdd, toUpdate, toRemove };
  },

  /**
   * Update comments with differential algorithm
   */
  async updateComments(comments: Comment_Base[], showLabels: boolean = false) {
    if (!window.map) return;

    // Initialize source if it doesn't exist
    if (!window.map.getSource(this._sourceId)) {
      const emptyGeojson: GeoJSONSourceSpecification = {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        } as GeoJSON.FeatureCollection,
      };
      window.map.addSource(this._sourceId, emptyGeojson);
      window.map.addLayer({
        id: this._layerId,
        source: this._sourceId,
        type: 'symbol',
        layout: {
          'icon-image': ['get', 'bitmapName'],
          'icon-size': 1,
          'icon-allow-overlap': true,
        },
      });

      // Move comment layer to top to ensure visibility over all basemap types
      this.moveLayerToTop();
    }

    // Handle label layer
    this.updateLabelLayer(showLabels);

    const { toAdd, toUpdate, toRemove } = this.computeCommentsDiff(comments);

    // Process removals
    for (const comment of toRemove) {
      await this.removeCommentFromMap(comment);
    }

    // Process additions and updates
    const commentsToProcess = [...toAdd, ...toUpdate];
    if (commentsToProcess.length > 0) {
      await this.addCommentsToMap(commentsToProcess);
    }

    // Update internal state
    this.private_commentsOnMap.clear();
    this.private_commentHashes.clear();

    for (const comment of comments) {
      this.private_commentsOnMap.set(comment.id!, comment);
      this.private_commentHashes.set(comment.id!, this.computeCommentHash(comment));
    }

    // Update source data with all current comments
    await this.updateSourceData(comments);

    // Ensure comment layer is on top after updates
    this.moveLayerToTop();

    // Update label overlays differentially if showing
    if (showLabels) {
      // Remove labels for deleted comments
      for (const comment of toRemove) {
        this.removeLabelOverlay(comment.id!);
      }

      // Update/create labels for added and updated comments
      for (const comment of [...toAdd, ...toUpdate]) {
        this.updateLabelOverlay(comment);
      }
    }
  },

  /**
   * Update the GeoJSON source data
   */
  async updateSourceData(comments: Comment_Base[]) {
    if (!window.map) return;

    const features: GeoJSON.Feature[] = [];

    for (const comment of comments) {
      try {
        const geometry = JSON.parse(comment.geojson!);

        // Normalize geometry type
        if (geometry.type === 'point') geometry.type = 'Point';
        else if (geometry.type === 'polygon') geometry.type = 'Polygon';
        else if (geometry.type === 'linestring') geometry.type = 'LineString';

        const bitmapName = await this.getBitmapForUser(comment);

        features.push({
          type: 'Feature',
          geometry: geometry,
          properties: {
            id: comment.id,
            title: comment.title,
            description: comment.description,
            resolved: comment.resolved,
            bitmapName: bitmapName,
          },
        });
      } catch (error) {
        console.error('Error processing comment geojson:', error);
      }
    }

    const geojsonData: GeoJSONSourceSpecification = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: features,
      } as GeoJSON.FeatureCollection,
    };

    const source = window.map.getSource(this._sourceId);
    if (source && source.type === 'geojson') {
      (source as maplibregl.GeoJSONSource).setData(geojsonData.data as GeoJSON.FeatureCollection);
    }
  },

  /**
   * Update label overlays visibility
   */
  updateLabelLayer(showLabels: boolean) {
    if (!window.map) return;

    if (showLabels) {
      this.createLabelOverlays();
    } else {
      this.removeLabelOverlays();
    }
  },

  /**
   * Create HTML div overlays for labels
   */
  createLabelOverlays() {
    if (!window.map) return;

    this.private_commentsOnMap.forEach((comment, commentId) => {
      this.updateLabelOverlay(comment);
    });
  },

  /**
   * Remove all label overlays
   */
  removeLabelOverlays() {
    this.private_labelOverlays.forEach((labelDiv, commentId) => {
      const marker = (labelDiv as any)._marker;
      if (marker) {
        marker.remove();
      }
    });
    this.private_labelOverlays.clear();
  },

  /**
   * Add comments to map (legacy API compatibility)
   */
  async addCommentsToMap(comments: Comment_Base[]) {
    // This method is now handled by updateComments for optimization
    // But we keep it for backward compatibility
    for (const comment of comments) {
      await this.getBitmapForUser(comment);
    }
  },

  /**
   * Remove comment from map
   */
  async removeCommentFromMap(comment: Comment_Base) {
    const commentId = comment.id!;

    // Remove from internal tracking
    this.private_commentsOnMap.delete(commentId);
    this.private_commentHashes.delete(commentId);

    // Remove label overlay if exists
    if (this.private_labelOverlays.has(commentId)) {
      const labelDiv = this.private_labelOverlays.get(commentId);
      const marker = (labelDiv as any)?._marker;
      if (marker) {
        marker.remove();
      }
      this.private_labelOverlays.delete(commentId);
    }
  },

  /**
   * Remove all comments from map
   */
  removeCommentsFromMap(comments: Comment_Base[]) {
    if (!window.map) return;

    // Clear internal state
    this.private_commentsOnMap.clear();
    this.private_commentHashes.clear();

    // Remove all label overlays
    this.removeLabelOverlays();

    // Clear source data
    const source = window.map.getSource(this._sourceId);
    if (source && source.type === 'geojson') {
      (source as maplibregl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: [],
      });
    }
  },

  /**
   * Update or create label overlay for specific comment
   */
  updateLabelOverlay(comment: Comment_Base) {
    if (!window.map || !comment.geojson) return;

    const commentId = comment.id!;

    // Remove existing overlay first
    this.removeLabelOverlay(commentId);

    try {
      const geometry = JSON.parse(comment.geojson);
      let coordinates: [number, number] | null = null;

      // Normalize geometry type and get coordinates
      if (geometry.type === 'point' || geometry.type === 'Point') {
        coordinates = geometry.coordinates;
      }

      if (coordinates) {
        // Create label div
        const labelDiv = document.createElement('div');
        labelDiv.className = 'comment-label-overlay';
        labelDiv.setAttribute('data-comment-id', String(commentId));
        labelDiv.innerHTML = `${renderToString(<PiChatCircleDots style={{ verticalAlign: '-1px', marginRight: 4, color: '#1677ff' }} />)}${comment.title || 'No Title'}`;
        labelDiv.style.cssText = `
          background: rgba(255, 255, 255, 0.92);
          border: 1px solid #cbd5e1;
          border-radius: 4px;
          padding: 2px 6px;
          font-size: 12px;
          color: #1e293b;
          white-space: nowrap;
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        `;
        labelDiv.onclick = (ev) => {
          ev.stopPropagation();
          window.dispatchEvent(
            new CustomEvent('geocloud:scroll-to-comment', { detail: { commentId } }),
          );
        };

        // Create MapLibre marker with the div
        const marker = new maplibregl.Marker({
          element: labelDiv,
          anchor: 'left',
          offset: [15, 0] // Offset to the right of the comment icon
        })
        .setLngLat(coordinates)
        .addTo(window.map);

        // Store both div and marker for cleanup
        this.private_labelOverlays.set(commentId, labelDiv);
        (labelDiv as any)._marker = marker;
      }
    } catch (error) {
      console.error('Error updating comment label overlay:', error);
    }
  },

  /**
   * Remove label overlay for specific comment
   */
  removeLabelOverlay(commentId: number) {
    if (this.private_labelOverlays.has(commentId)) {
      const labelDiv = this.private_labelOverlays.get(commentId);
      const marker = (labelDiv as any)?._marker;
      if (marker) {
        marker.remove();
      }
      this.private_labelOverlays.delete(commentId);
    }
  },

  /**
   * Move comment layer to top for visibility over all basemap types
   */
  moveLayerToTop() {
    if (!window.map || !window.map.getLayer(this._layerId)) return;

    try {
      // Move comment layer to top (no beforeId means move to top)
      window.map.moveLayer(this._layerId);
    } catch (error) {
      console.warn('Error moving comment layer to top:', error);
    }
  },

  /**
   * Clear all resources
   */
  cleanup() {
    if (!window.map) return;

    // Remove label overlays
    this.removeLabelOverlays();

    // Remove layers and source
    if (window.map.getLayer(this._layerId)) {
      window.map.removeLayer(this._layerId);
    }
    if (window.map.getSource(this._sourceId)) {
      window.map.removeSource(this._sourceId);
    }

    // Clear caches
    this.private_commentsOnMap.clear();
    this.private_commentHashes.clear();
    this.private_bitmapCache.clear();
  },
};